import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { format } from 'date-fns';

export interface IHistoryItem {
  id: string;
  type: string;
  attributes: {
    action: 'CREATED' | 'UPDATED' | 'DELETED';
    created_at: string;
    user: {
      id: string;
      full_name: string;
    };
    data: Record<string, any>;
  };
}

interface HistoryProps {
  title?: string;
  data: IHistoryItem[];
  isLoading?: boolean;
}

const actionColors = {
  CREATED: 'success',
  UPDATED: 'warning',
  DELETED: 'error',
} as const;

interface ChangeDataProps {
  data: Record<string, any>;
}

const ChangeData = ({ data }: ChangeDataProps) => {
  const formatValue = (value: any): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  return (
    <div style={{ fontSize: '0.875rem' }}>
      {Object.entries(data).map(([key, value]) => (
        <div
          key={key}
          style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}
        >
          <span style={{ fontWeight: 500, color: 'rgba(0, 0, 0, 0.45)' }}>
            {key}:
          </span>
          <span>{formatValue(value)}</span>
        </div>
      ))}
    </div>
  );
};

export const History = ({
  title = 'History',
  data,
  isLoading,
}: HistoryProps) => {
  if (isLoading) {
    return <Typography.Text>Loading history...</Typography.Text>;
  }

  if (!data?.length) {
    return <Typography.Text>No history available</Typography.Text>;
  }

  const columns: ColumnsType<IHistoryItem> = [
    {
      title: 'Action',
      dataIndex: ['attributes', 'action'],
      key: 'action',
      render: (action: 'CREATED' | 'UPDATED' | 'DELETED') => (
        <Tag color={actionColors[action]}>{action}</Tag>
      ),
    },
    {
      title: 'User',
      dataIndex: ['attributes', 'user', 'full_name'],
      key: 'user',
    },
    {
      title: 'Changes',
      dataIndex: ['attributes', 'data'],
      key: 'changes',
      render: (data: Record<string, any>) => <ChangeData data={data} />,
    },
    {
      title: 'Date',
      dataIndex: ['attributes', 'created_at'],
      key: 'date',
      width: 200,
      render: (date: string) => format(new Date(date), 'PPpp'),
    },
  ];

  return (
    <div style={{ width: '100%', marginTop: '16px' }}>
      <Typography.Title level={5} style={{ marginBottom: '16px' }}>
        {title}
      </Typography.Title>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={false}
        bordered
        rowHoverable={false}
      />
    </div>
  );
};
